import React from 'react';

import { Spinner } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useLongPress } from 'use-long-press';
import { isEmpty } from 'lodash';
import HorizontalScroll from '../../my-page/components/HorizontalScroll';
import NewPhotoCard from './NewPhotoCard';
import { Virtuoso } from 'react-virtuoso';
import styled from '@emotion/styled';
import type { EventType, PhotoCardType } from '../../types/PhotoCardType';

const EventNameBoxWrapper = styled.div`
  overflow: hidden;
  padding-top: 20px;
  padding-bottom: 9px;
  padding-left: 24px;
  padding-right: 24px;
`;

type PhotoCardsByEventViewerProps = {
  events: EventType[];
  onLongClickedPhotoCard: (photoCard: PhotoCardType) => void;
  endReached: () => void;
  isNextPageLoading: boolean;
};

export default function PhotoCardsByEventViewer({
  events,
  onLongClickedPhotoCard,
  endReached,
  isNextPageLoading
}: PhotoCardsByEventViewerProps) {
  // #region draw cards
  const longPressHandler = useLongPress((_e, { context: photoCard }) => {
    onLongClickedPhotoCard(photoCard as PhotoCardType);
  });

  const drawCardsWithEventName = (e: EventType) => {
    if (isEmpty(e.photoCards)) {
      return null;
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: -1 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -1 }}
        transition={{ duration: 0.5 }}
      >
        <EventNameBoxWrapper>
          <EventTitle event={e} />
        </EventNameBoxWrapper>

        <HorizontalScroll className="w-full">
          <div className="flex gap-[6px] first:ml-[24px] last:mr-[24px]">
            {e.photoCards?.map(
              (photoCard) =>
                photoCard && (
                  <NewPhotoCard
                    key={photoCard.photoCardId}
                    photoCard={photoCard}
                    photoCardSize="sm"
                    {...longPressHandler(photoCard)}
                  />
                )
            )}
          </div>
        </HorizontalScroll>
      </motion.div>
    );
  };

  return (
    <Virtuoso
      useWindowScroll
      style={{ height: '100%' }}
      endReached={endReached}
      overscan={150}
      atBottomThreshold={100}
      data={events}
      itemContent={(_, e) => (
        <motion.div
          key={e.eventId}
          initial={{ opacity: 0, y: -1 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -1 }}
          transition={{ duration: 0.5 }}
        >
          {drawCardsWithEventName(e)}
        </motion.div>
      )}
      components={{
        Footer: () => isNextPageLoading && null
      }}
    />
  );
}

//@TODO : 로딩스피너 QA때 추가할지 이야기해보고 추가 하지말라고하면 로딩스피너 관련 상태,  Footer: 원상복구
// 추가하라고 하면 밑에 코드 null부분하고 치환
// (
//             <div className="w-full flex justify-center py-3 pb-[90px]">
//               <Spinner className="text-primary-light dark:text-primary-dark" size="sm" />
//             </div>
//           )

const EventTitle = ({ event }: { event: EventType }) => {
  return (
    <div className="flex items-center gap-[6px] mb-[3px] mt-[2px]">
      <img
        width={24}
        height={24}
        src={`/assets/icons/eventCategory/ic_event_${event.category}.png`}
        alt={event.category}
      />
      <p className="text-[13px] font-medium leading-normal text-main-light-2 dark:text-main-dark-2">
        {event.name}
      </p>
    </div>
  );
};
